import React, { useState, useEffect } from 'react';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useApp } from '../../AppContext';
import SideHustleLayout from '../../components/SideHustleLayout';

interface Dream {
  idea: string;
  createdAt: Date;
}

const DreamingHub: React.FC = () => {
  const { currentUser } = useApp();
  const [dreams, setDreams] = useState<Dream[]>([]);
  const [newDream, setNewDream] = useState('');
  const [saving, setSaving] = useState(false);

  // Edit state
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editDream, setEditDream] = useState('');

  // Load data from Firebase
  useEffect(() => {
    const loadData = async () => {
      if (!currentUser) return;

      try {
        const docRef = doc(db, 'sideHustleUsers', currentUser.uid);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          setDreams(data.dreams || []);
        }
      } catch (error) {
        console.error('Error loading data:', error);
      }
    };

    loadData();
  }, [currentUser]);

  // Save data to Firebase
  const saveData = async (updatedDreams: Dream[]) => {
    if (!currentUser) return;

    setSaving(true);
    try {
      const docRef = doc(db, 'sideHustleUsers', currentUser.uid);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        await setDoc(docRef, {
          ...docSnap.data(),
          dreams: updatedDreams,
          lastUpdated: new Date(),
        });
      } else {
        await setDoc(docRef, {
          userId: currentUser.uid,
          email: currentUser.email,
          createdAt: new Date(),
          lastUpdated: new Date(),
          dreams: updatedDreams,
        });
      }
    } catch (error) {
      console.error('Error saving data:', error);
    } finally {
      setSaving(false);
    }
  };

  const addDream = () => {
    if (newDream.trim()) {
      const updated = [
        ...dreams,
        {
          idea: newDream.trim(),
          createdAt: new Date(),
        },
      ];
      setDreams(updated);
      setNewDream('');
      saveData(updated);
    }
  };

  const removeDream = (index: number) => {
    const updated = dreams.filter((_, i) => i !== index);
    setDreams(updated);
    saveData(updated);
  };

  // Edit dream functions
  const startEditing = (index: number) => {
    setEditingIndex(index);
    setEditDream(dreams[index].idea);
  };

  const cancelEditing = () => {
    setEditingIndex(null);
    setEditDream('');
  };

  const saveEdit = () => {
    if (editingIndex !== null && editDream.trim()) {
      const updated = [...dreams];
      updated[editingIndex] = {
        ...updated[editingIndex],
        idea: editDream.trim(),
      };
      setDreams(updated);
      saveData(updated);
      setEditingIndex(null);
      setEditDream('');
    }
  };

  return (
    <SideHustleLayout currentPage="dreams">
      <div className="flex flex-col items-center p-6 min-h-[calc(100vh-80px)]">
        <div className="max-w-4xl w-full">
          <h1 className="text-5xl font-bold text-center mb-6">The Dreaming Hub</h1>
          <p className="text-xl text-gray-700 text-center mb-4">
            Big ideas, future goals, or just brainstorming.
          </p>
          <p className="text-lg text-gray-600 text-center mb-12">
            A place to put your notes
          </p>

          {/* Add New Dream Button */}
          <div className="flex flex-col items-center mb-8">
            <div className="w-full max-w-md mb-6">
              <textarea
                value={newDream}
                onChange={(e) => setNewDream(e.target.value)}
                placeholder="Enter your dream or idea..."
                rows={3}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary resize-none mb-3"
              />
              <button
                onClick={addDream}
                disabled={!newDream.trim()}
                className="w-full bg-[#86C87B] text-black px-8 py-4 rounded-3xl shadow-lg hover:bg-[#7AB76F] transition-all font-semibold text-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Add a new idea
              </button>
            </div>
          </div>

          {/* Dreams Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {dreams.map((dream, index) => (
              <div
                key={index}
                className="bg-primary p-6 rounded-3xl shadow-lg text-white relative min-h-[120px] flex items-center justify-center"
              >
                {editingIndex === index ? (
                  <div className="w-full space-y-3">
                    <textarea
                      value={editDream}
                      onChange={(e) => setEditDream(e.target.value)}
                      rows={3}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-white resize-none text-gray-800"
                      autoFocus
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={saveEdit}
                        className="flex-1 bg-white text-primary px-4 py-2 rounded-xl hover:bg-gray-100 font-semibold"
                      >
                        Save
                      </button>
                      <button
                        onClick={cancelEditing}
                        className="flex-1 bg-gray-400 text-white px-4 py-2 rounded-xl hover:bg-gray-500 font-semibold"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="absolute top-4 right-4 flex gap-2">
                      <button
                        onClick={() => startEditing(index)}
                        className="text-white hover:text-gray-200 font-semibold"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => removeDream(index)}
                        className="text-white hover:text-red-200 font-bold text-xl"
                      >
                        ×
                      </button>
                    </div>
                    <p className="text-xl font-semibold text-center pr-24">
                      {dream.idea}
                    </p>
                  </>
                )}
              </div>
            ))}
          </div>

          {dreams.length === 0 && (
            <div className="text-center text-gray-600 mt-8">
              <p className="text-lg">
                No dreams added yet. Start by adding your first big idea!
              </p>
            </div>
          )}

          {saving && (
            <div className="text-center mt-6 text-primary font-semibold">
              Saving...
            </div>
          )}
        </div>
      </div>
    </SideHustleLayout>
  );
};

export default DreamingHub;
